import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { Permission } from 'extra/types/Permissions'
import type { TDeploymentCacheStatus } from 'extra/types/ApiModels'
import { api } from '../api/client'
import { ExecutionsList } from '../components/ExecutionsList'
import { useEntities } from '../context/EntitiesContext'
import { useAuth } from '../context/AuthContext'
import { PromptPageHeader } from '../components/PromptPageHeader'
import { PromptPageNavigation } from '../components/PromptPageNavigation'
import { RequirePermission } from '../components/RequirePermission'
import { extractErrorMessage } from '../utils/extractErrorMessage'

const STATUS_COLORS: Record<TDeploymentCacheStatus, string> = {
  disabled: 'bg-gray-700 text-gray-300',
  pending: 'bg-yellow-900 text-yellow-200',
  active: 'bg-green-900 text-green-200',
  error: 'bg-red-900 text-red-200',
}

function CacheStatusBadge ({ status }: { status: TDeploymentCacheStatus }) {
  const cls = STATUS_COLORS[status] ?? STATUS_COLORS.disabled
  return <span className={`px-2 py-0.5 rounded text-xs font-mono ${cls}`}>{status}</span>
}

function formatTs (iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export function DeploymentPage () {
  const { id } = useParams<{ id: string }>()
  const { hasPermission } = useAuth()
  const { prompts, folders, deployments: allDeployments, loading: entitiesLoading, refreshDeployments } = useEntities()
  const canManageDeployments = hasPermission(Permission.MANAGE_DEPLOYMENTS)

  const prompt = id ? prompts.find(p => p.id === id) || null : null
  const currentFolder = prompt?.folderId ? folders.find(f => f.id === prompt.folderId) : null
  const deployments = useMemo(() => id ? allDeployments.filter(d => d.promptId === id) : [], [allDeployments, id])

  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | null>(null)

  // Edit state
  const [editLabel, setEditLabel] = useState('')
  const [savingDeployment, setSavingDeployment] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Cache state
  const [cacheEnabled, setCacheEnabled] = useState(false)
  const [cacheTtlSeconds, setCacheTtlSeconds] = useState(86400)
  const [savingCache, setSavingCache] = useState(false)
  const [cacheError, setCacheError] = useState<string | null>(null)

  // Tab state
  const [activeTab, setActiveTab] = useState<'executions' | 'settings'>('executions')

  // Auto-select first deployment
  useEffect(() => {
    if (entitiesLoading) return
    if (deployments.length > 0 && !deployments.some(d => d.id === selectedDeploymentId)) {
      setSelectedDeploymentId(deployments[0].id)
    }
  }, [deployments, entitiesLoading])

  const selectedDeployment = deployments.find(d => d.id === selectedDeploymentId) || null

  // Sync edit state when selected deployment changes
  useEffect(() => {
    if (selectedDeployment) {
      setEditLabel(selectedDeployment.label || '')
      setSaveError(null)
      setCacheEnabled(selectedDeployment.cache?.enabled === true)
      setCacheTtlSeconds(selectedDeployment.cache?.ttlSeconds ?? 86400)
      setCacheError(null)
    }
  }, [selectedDeployment?.id])

  const handleSaveDeployment = async () => {
    if (!selectedDeployment) return

    setSavingDeployment(true)
    setSaveError(null)

    try {
      await api.deployments.update(selectedDeployment.id, {
        label: editLabel.trim() || null,
      })
      await refreshDeployments()
    } catch (err) {
      setSaveError(await extractErrorMessage(err, 'Failed to save deployment'))
    } finally {
      setSavingDeployment(false)
    }
  }

  const handleSaveCache = async () => {
    if (!selectedDeployment) return
    setSavingCache(true)
    setCacheError(null)
    try {
      await api.deployments.update(selectedDeployment.id, {
        cache: { enabled: cacheEnabled, ttlSeconds: cacheTtlSeconds },
      })
      await refreshDeployments()
    } catch (err) {
      setCacheError(await extractErrorMessage(err, 'Failed to update caching'))
    } finally {
      setSavingCache(false)
    }
  }

  const cacheCompatibility = (() => {
    if (!selectedDeployment) return { supported: false, reason: 'No deployment selected' }
    const config = selectedDeployment.variantSnapshot.config as Record<string, any> | null | undefined
    const model = typeof config?.model === 'string' ? config.model : ''
    if (!model.includes('gemini')) {
      return { supported: false, reason: 'Caching is only supported for Gemini models' }
    }
    const tools = config?.tools as Array<Record<string, any>> | undefined
    if (Array.isArray(tools) && tools.some(t => 'googleSearch' in t || 'urlContext' in t)) {
      return { supported: false, reason: 'Caching is incompatible with google_search / url_context tools' }
    }
    if (config?._format !== 'gemini-native') {
      return { supported: false, reason: 'Caching requires a Gemini-native variant config' }
    }
    return { supported: true, reason: '' }
  })()

  const handleDeleteDeployment = async () => {
    if (!selectedDeployment) return

    const confirmed = window.confirm(`Are you sure you want to delete the deployment "${selectedDeployment.label || selectedDeployment.id}"?`)
    if (!confirmed) return

    setSavingDeployment(true)
    setSaveError(null)

    try {
      await api.deployments.delete(selectedDeployment.id)
      await refreshDeployments()
      const remaining = deployments.filter(d => d.id !== selectedDeployment.id)
      setSelectedDeploymentId(remaining.length > 0 ? remaining[0].id : null)
    } catch (err) {
      setSaveError(await extractErrorMessage(err, 'Failed to delete deployment'))
    } finally {
      setSavingDeployment(false)
    }
  }

  if (entitiesLoading) {
    return (
      <div>
        <div className="text-gray-400">Loading...</div>
      </div>
    )
  }

  if (!prompt) {
    return (
      <div>
        <div className="alert-error">Prompt not found</div>
      </div>
    )
  }

  return (
    <div>
      <PromptPageHeader prompt={prompt} currentFolder={currentFolder} />
      <PromptPageNavigation promptId={id!} />

      {/* Deployment Selection */}
      <div className="mb-4">
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="deployment-select" className="input-label">
              Deployment
            </label>
            <select
              id="deployment-select"
              value={selectedDeploymentId || ''}
              onChange={(e) => setSelectedDeploymentId(e.target.value || null)}
              className="input"
            >
              {deployments.length === 0
                ? (
                <option value="">No deployments</option>
                  )
                : (
                    deployments.map((deployment) => (
                  <option key={deployment.id} value={deployment.id}>
                    {deployment.label || `Deployment ${deployment.id.slice(-8)}`}
                  </option>
                    ))
                  )}
            </select>
          </div>

          {selectedDeployment && (
            <div className="flex-1 max-w-md">
              <label className="input-label">Deployment ID</label>
              <div className="text-gray-300 font-mono text-sm bg-gray-700 px-3 py-1.5 rounded-lg border border-gray-600 truncate">
                {selectedDeployment.id}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Deployment Details */}
      {selectedDeployment ? (
        <div>
          {/* Sub Navigation */}
          <div className="mb-4 border-b border-gray-800">
            <nav className="flex gap-4">
              <button
                onClick={() => setActiveTab('executions')}
                className={`px-3 py-1.5 text-sm transition-colors ${activeTab === 'executions' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
              >
                Executions
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-3 py-1.5 text-sm transition-colors ${activeTab === 'settings' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
              >
                Settings
              </button>
            </nav>
          </div>

          {activeTab === 'executions' ? (
            <RequirePermission permission={Permission.VIEW_EXECUTIONS}>
              <div className="border border-gray-800 rounded-lg bg-gray-900">
                <ExecutionsList deploymentId={selectedDeployment.id} promptId={id} />
              </div>
            </RequirePermission>
          ) : (
            <div className="space-y-4">
              {saveError && (
                <div className="alert-error">{saveError}</div>
              )}

              {/* Editable Label */}
              <div>
                <label htmlFor="deployment-label" className="input-label">
                  Label
                </label>
                <input
                  id="deployment-label"
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  className="input"
                  placeholder="Deployment label"
                  readOnly={!canManageDeployments}
                />
              </div>

              {/* Variant Snapshot (read-only) */}
              <div>
                <label className="input-label">Variant Snapshot</label>
                <textarea
                  readOnly
                  value={JSON.stringify(selectedDeployment.variantSnapshot, null, 2)}
                  className="input font-mono text-sm bg-gray-800 field-sizing-content resize-none overflow-hidden"
                  rows={12}
                />
              </div>

              {/* Caching */}
              <div className="border border-gray-800 rounded-lg p-4 bg-gray-900">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">Gemini context caching</h3>
                  <CacheStatusBadge status={selectedDeployment.cache?.status ?? 'disabled'} />
                </div>

                {cacheError && <div className="alert-error mb-3">{cacheError}</div>}

                <div className="flex items-center gap-3 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cacheEnabled}
                      onChange={(e) => setCacheEnabled(e.target.checked)}
                      disabled={!canManageDeployments || !cacheCompatibility.supported}
                    />
                    <span className="text-sm">Enable caching</span>
                  </label>
                  {!cacheCompatibility.supported && (
                    <span className="text-xs text-yellow-400" title={cacheCompatibility.reason}>{cacheCompatibility.reason}</span>
                  )}
                </div>

                <div className="flex items-end gap-3 mb-3">
                  <div className="max-w-xs">
                    <label htmlFor="cache-ttl" className="input-label">TTL (seconds)</label>
                    <input
                      id="cache-ttl"
                      type="number"
                      min={60}
                      step={60}
                      value={cacheTtlSeconds}
                      onChange={(e) => setCacheTtlSeconds(Math.max(60, parseInt(e.target.value || '0', 10) || 60))}
                      className="input"
                      readOnly={!canManageDeployments || !cacheCompatibility.supported}
                    />
                  </div>
                  <RequirePermission permission={Permission.MANAGE_DEPLOYMENTS}>
                    <button
                      type="button"
                      onClick={handleSaveCache}
                      disabled={savingCache || !cacheCompatibility.supported}
                      className="btn btn-primary"
                    >
                      {savingCache ? 'Saving...' : 'Apply caching'}
                    </button>
                  </RequirePermission>
                </div>

                {selectedDeployment.cache && (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-300">
                    <dt className="text-gray-400">Cached messages</dt>
                    <dd className="font-mono">{selectedDeployment.cache.cachedMessageCount}</dd>
                    <dt className="text-gray-400">Estimated tokens</dt>
                    <dd className="font-mono">{selectedDeployment.cache.estimatedTokens ?? '—'}</dd>
                    <dt className="text-gray-400">Cache name</dt>
                    <dd className="font-mono truncate" title={selectedDeployment.cache.name ?? ''}>{selectedDeployment.cache.name ?? '—'}</dd>
                    <dt className="text-gray-400">Model</dt>
                    <dd className="font-mono">{selectedDeployment.cache.model ?? '—'}</dd>
                    <dt className="text-gray-400">Last refreshed</dt>
                    <dd className="font-mono">{formatTs(selectedDeployment.cache.lastRefreshedAt)}</dd>
                    <dt className="text-gray-400">Expires</dt>
                    <dd className="font-mono">{formatTs(selectedDeployment.cache.expiresAt)}</dd>
                    {selectedDeployment.cache.lastError && (
                      <>
                        <dt className="text-gray-400">Last error</dt>
                        <dd className="text-red-300 break-words">{selectedDeployment.cache.lastError}</dd>
                      </>
                    )}
                  </dl>
                )}
              </div>

              {/* Save and Delete Buttons */}
              <RequirePermission permission={Permission.MANAGE_DEPLOYMENTS}>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSaveDeployment}
                    disabled={savingDeployment}
                    className="btn btn-primary"
                  >
                    {savingDeployment ? 'Saving...' : 'Save'}
                  </button>
                  <div className="grow"/>
                  <button
                    type="button"
                    onClick={handleDeleteDeployment}
                    disabled={savingDeployment}
                    className="btn btn-danger-light"
                  >
                    Delete Deployment
                  </button>
                </div>
              </RequirePermission>
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-400">
          No deployments yet. Deploy a variant from the Variants tab to create one.
        </div>
      )}
    </div>
  )
}
